/**
 * channel-label.ts — Phân biệt KÊNH trên dòng hội thoại khi doanh nghiệp có nhiều tài khoản cùng loại
 * (nhiều Zalo OA, nhiều Fanpage, nhiều Zalo cá nhân…): chấm logo ở góc ảnh chỉ cho biết loại kênh, nên hiện thêm
 * TÊN tài khoản (tên OA / Fanpage). Chỉ một tài khoản của loại đó → không cần (giữ dòng gọn).
 */
import { groupOfPlatform, type ChannelGroupId } from '@/lib/channel-groups'
import { platformLabelOf } from '@/lib/platform-labels'

/** Nhóm kênh có từ 2 tài khoản trở lên → dòng hội thoại thuộc nhóm đó cần hiện tên tài khoản. */
export function groupsWithManyAccounts(accounts: Iterable<{ platform: number }>): Set<ChannelGroupId> {
  const count = new Map<ChannelGroupId, number>()
  for (const a of accounts) {
    const g = groupOfPlatform(a.platform)
    count.set(g, (count.get(g) ?? 0) + 1)
  }
  return new Set([...count].filter(([, n]) => n > 1).map(([g]) => g))
}

/** Tên tài khoản kênh cần hiện trên dòng (null = không cần / không có tên). */
export function accountLabel(
  account: { platform: number; displayName?: string | null } | null | undefined,
  manyGroups: ReadonlySet<ChannelGroupId>,
): string | null {
  if (!account || !manyGroups.has(groupOfPlatform(account.platform))) return null
  const name = account.displayName?.trim()
  return name || null
}

/** Chú thích khi rê chuột lên logo / nhãn kênh: "Zalo OA · Phường Sơn Tây". */
export function channelTitle(account: { platform: number; displayName?: string | null } | null | undefined): string | undefined {
  if (!account) return undefined
  const name = account.displayName?.trim()
  return name ? `${platformLabelOf(account.platform)} · ${name}` : platformLabelOf(account.platform)
}

/**
 * Màu nhãn tài khoản (kiểu Lark) — tô theo ID tài khoản để CÙNG một tài khoản luôn ra CÙNG một màu (ổn định
 * giữa các lần tải trang / giữa các hội thoại), không cần lưu màu riêng ở CSDL.
 */
const ACCOUNT_COLOR_PALETTE = ['#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777', '#0891b2', '#65a30d']
export function accountColor(accountId: string): string {
  let hash = 0
  for (let i = 0; i < accountId.length; i++) hash = (hash * 31 + accountId.charCodeAt(i)) >>> 0
  return ACCOUNT_COLOR_PALETTE[hash % ACCOUNT_COLOR_PALETTE.length]
}
